import Loader from '@web/components/loader';
import { useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { useTechlineCase } from './hooks/use-techline-case';
import { CidCvnWorksheetSubview } from './subviews/cid-cvn-worksheet-subview/cid-cvn-worksheet-subview';
import { DraftCaseSubview } from './subviews/draft-case-subview/draft-case-subview';
import { SubmittedCaseSubview } from './subviews/submitted-case-subview/submitted-case-subview';
import {
    type WorksheetBackOptions,
    WorksheetSubmissionSubview,
} from './subviews/worksheet-submission-subview/worksheet-submission-subview';
import { type WorksheetOpenMode } from './worksheet-group-accordion';

/**
 * Routed view for managing a single techline case. Owns all case state
 * via the useTechlineCase composition hook and threads data and callbacks
 * down to the appropriate subview based on case status and active route.
 *
 * @returns the case management view
 */
export function CaseManagementView() {
    // State
    const { caseNumber } = useParams();
    const [, setSearchParams] = useSearchParams();

    // Hooks
    const {
        // State
        allowCaseFeatures,
        assetData,
        assetErrorMessage,
        assignmentData,
        availableGroupError,
        clearWorksheetAssetPolling,
        commentData,
        dtcCodeData,
        dtcErrorMessage,
        editingWorksheet,
        failCodeData,
        failCodeErrors,
        isCommentLoading,
        isDtcLoading,
        isFailCodeSaving,
        isLoadingAssignments,
        isLoadingAvailableGroups,
        isLoadingWorksheets,
        isPrimaryDtcUpdating,
        showFailCodeModal,
        techlineCaseData,
        userName,
        vehicleVinData,
        worksheetGroupData,
        worksheetSubmissionData,
        worksheetSubmissionFetching,
        worksheetTitleData,
        worksheetValidationResults,

        // Draft stepper validation
        draftStepperValidation,

        // Derived
        canEditDraft,
        canEditSubmittedCase,
        enrichedWorksheetGroupData,
        isInitialDataLoading,
        primaryRetailerData,

        // Setters
        setEditingWorksheet,

        // Data fetchers
        refreshAssets,
        refreshWorksheetData,

        // Handlers
        handleAssetErrorDismiss,
        handleFetchWorksheetForm,
        handleFetchWorksheetSubmission,
        handleFetchWorksheetTitles,
        handleHideWorksheetFilesFromTechShare,
        handleSubmitWorksheetWithAssets,
        handleTechlineCaseDataChange,

        // Mutations
        addComment,
        closeCase,
        deleteCase,
        refreshAssignments,
        refreshComments,
        saveCase,
        submitCase,
        toggleAssignmentCompletion,

        // Asset mutations
        deleteAsset,
        updateAsset,
        uploadAsset,

        // Fail code mutations
        clearFailCode,
        closeFailCodeModal,
        openFailCodeModal,
        updateFailCode,

        // Team mutations
        addTeamMember,

        // Worksheet submission mutations
        handleDeleteDraftWorksheetSubmission,

        // CID-CVN mutations
        deleteCidCvnCase,
        saveCidCvnCase,
        submitCidCvnCase,

        // DTC Codes mutations
        handleAddDtc,
        handleDeleteDtc,
        handlePrimaryChange,
        handleToggleFreezeFrame,
    } = useTechlineCase(caseNumber);

    // Handlers

    /**
     * Closes the worksheet editor and optionally refreshes worksheet data.
     * When shouldRefresh is true (after a save or submit), refreshing happens
     * before the editor closes so the spinner persists until all data is ready.
     * When shouldRefresh is false (back without saving), the editor closes instantly.
     *
     * @param options - optional flags controlling the back behavior
     */
    const handleWorksheetSubmissionBack = useCallback(
        async (options?: WorksheetBackOptions) => {
            if (options?.shouldRefresh) {
                await refreshWorksheetData();
            }
            setSearchParams({ expandWorksheets: 'true', step: '2' });
            setEditingWorksheet(null);
        },
        [refreshWorksheetData, setEditingWorksheet, setSearchParams]
    );

    /**
     * Opens a worksheet for editing by setting the editing worksheet state with the current case status.
     *
     * @param worksheetId - the worksheet definition ID
     * @param submissionId - the worksheet submission ID (if editing an existing submission)
     */
    const handleOpenWorksheet = useCallback(
        (worksheetId: string, submissionId: string | undefined, mode: WorksheetOpenMode) => {
            if (!techlineCaseData) {
                return;
            }
            setEditingWorksheet({
                caseStatus: techlineCaseData.techlineStatusCode,
                submissionId,
                worksheetId,
                mode,
            });
        },
        [setEditingWorksheet, techlineCaseData]
    );

    // JSX

    // A worksheet is open for editing; render it inline instead of the case view
    if (editingWorksheet && caseNumber) {
        return (
            <WorksheetSubmissionSubview
                caseNumber={caseNumber}
                caseStatus={editingWorksheet.caseStatus}
                onBack={handleWorksheetSubmissionBack}
                onClearWorksheetAssetPolling={clearWorksheetAssetPolling}
                onDeleteDraftWorksheetSubmission={handleDeleteDraftWorksheetSubmission}
                onFetchWorksheetForm={handleFetchWorksheetForm}
                onFetchWorksheetSubmission={handleFetchWorksheetSubmission}
                onHideWorksheetFilesFromTechShare={handleHideWorksheetFilesFromTechShare}
                onSubmitWorksheetWithAssets={handleSubmitWorksheetWithAssets}
                submissionId={editingWorksheet.submissionId}
                userName={userName}
                worksheetId={editingWorksheet.worksheetId}
                worksheetSubmissionData={worksheetSubmissionData}
                worksheetSubmissionFetching={worksheetSubmissionFetching}
            />
        );
    }

    // Still waiting for initial data (case details + worksheet submissions) to load
    if (isInitialDataLoading) {
        return <Loader />;
    }

    // CID/CVN cases in a DRAFT status get a unique single-page view.
    if (caseNumber && techlineCaseData && techlineCaseData.isDraft) {
        if (techlineCaseData.isCidCvnCase) {
            return (
                <CidCvnWorksheetSubview
                    assetData={assetData}
                    assetErrorMessage={assetErrorMessage}
                    caseDetails={techlineCaseData}
                    caseNumber={caseNumber}
                    onAssetErrorDismiss={handleAssetErrorDismiss}
                    onDeleteAsset={deleteAsset}
                    onDeleteCase={deleteCidCvnCase}
                    onFetchWorksheetSubmission={handleFetchWorksheetSubmission}
                    onRefreshAssets={refreshAssets}
                    onSaveCase={saveCidCvnCase}
                    onSubmitCase={submitCidCvnCase}
                    onUploadAsset={uploadAsset}
                    primaryRetailerData={primaryRetailerData}
                    vehicleVinData={vehicleVinData}
                    worksheetSubmissionData={worksheetSubmissionData}
                    worksheetSubmissionFetching={worksheetSubmissionFetching}
                />
            );
        }

        // DRAFT cases get the multi-step stepper editor; all other statuses get the read-mostly Review view
        return (
            <DraftCaseSubview
                assetData={assetData}
                assetErrorMessage={assetErrorMessage}
                availableGroupError={availableGroupError}
                availableWorksheetGroupData={enrichedWorksheetGroupData}
                canEditDraft={canEditDraft}
                caseDetails={techlineCaseData}
                caseNumber={caseNumber}
                draftStepperValidation={draftStepperValidation}
                dtcCodeData={dtcCodeData}
                dtcErrorMessage={dtcErrorMessage}
                isDtcLoading={isDtcLoading}
                isLoadingAvailableGroups={isLoadingAvailableGroups}
                isPrimaryDtcUpdating={isPrimaryDtcUpdating}
                onAddDtc={handleAddDtc}
                onAddTeamMember={addTeamMember}
                onAssetErrorDismiss={handleAssetErrorDismiss}
                onCaseDetailsChange={handleTechlineCaseDataChange}
                onDeleteAsset={deleteAsset}
                onDeleteCase={deleteCase}
                onDeleteDraftWorksheetSubmission={handleDeleteDraftWorksheetSubmission}
                onDeleteDtc={handleDeleteDtc}
                onFetchWorksheetTitles={handleFetchWorksheetTitles}
                onOpenWorksheet={handleOpenWorksheet}
                onPrimaryChange={handlePrimaryChange}
                onRefreshAssets={refreshAssets}
                onSaveCase={saveCase}
                onSubmitCase={submitCase}
                onToggleFreezeFrame={handleToggleFreezeFrame}
                onUpdateAsset={updateAsset}
                onUploadAsset={uploadAsset}
                primaryRetailerData={primaryRetailerData}
                vehicleVinData={vehicleVinData}
                worksheetValidationResults={worksheetValidationResults}
            />
        );
    }

    // Submitted, in-review, or closed cases; read-mostly view with comments sidebar
    if (!caseNumber) {
        return null;
    }

    return (
        <SubmittedCaseSubview
            allowCaseFeatures={allowCaseFeatures}
            assetData={assetData}
            assetErrorMessage={assetErrorMessage}
            assignmentData={assignmentData}
            canEditSubmittedCase={canEditSubmittedCase}
            caseDetails={techlineCaseData}
            caseNumber={caseNumber}
            commentData={commentData}
            dtcCodeData={dtcCodeData}
            failCodeData={failCodeData}
            failCodeErrors={failCodeErrors}
            isCommentLoading={isCommentLoading}
            isFailCodeSaving={isFailCodeSaving}
            isLoadingAssignments={isLoadingAssignments}
            isLoadingWorksheets={isLoadingWorksheets}
            onAddComment={addComment}
            onAddTeamMember={addTeamMember}
            onAssetErrorDismiss={handleAssetErrorDismiss}
            onClearFailCode={clearFailCode}
            onCloseCase={closeCase}
            onCloseFailCodeModal={closeFailCodeModal}
            onDeleteAsset={deleteAsset}
            onDeleteDraftWorksheetSubmission={handleDeleteDraftWorksheetSubmission}
            onFetchWorksheetTitles={handleFetchWorksheetTitles}
            onOpenFailCodeModal={openFailCodeModal}
            onOpenWorksheet={handleOpenWorksheet}
            onRefreshAssets={refreshAssets}
            onRefreshAssignments={refreshAssignments}
            onRefreshComments={refreshComments}
            onToggleAssignmentCompletion={toggleAssignmentCompletion}
            onUpdateAsset={updateAsset}
            onUpdateFailCode={updateFailCode}
            onUploadAsset={uploadAsset}
            primaryRetailerData={primaryRetailerData}
            showFailCodeModal={showFailCodeModal}
            vehicleVinData={vehicleVinData}
            worksheetGroupData={worksheetGroupData ?? null}
            worksheetTitleData={worksheetTitleData}
            worksheetValidationResults={worksheetValidationResults}
        />
    );
}
